import { redirect } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import z from 'zod';

import { sessionMiddleware } from '@/middleware/session';
import { updateSessionDataHeaders } from '@/utils/session';

export const selectTenantServerFn = createServerFn({ method: 'POST' })
  .middleware([sessionMiddleware])
  .inputValidator(z.object({ tenant_id: z.string().optional() }))
  .handler(async ({ data: { tenant_id }, context: { session } }) => {
    if (session.tenantId === tenant_id) {
      return Response.json({ success: true });
    }

    return new Response(JSON.stringify({ success: true }), {
      headers: updateSessionDataHeaders(new Headers(), {
        ...session,
        tenantId: tenant_id,
      }),
    });
  });

/**
 * Tenant-switch variant intended for use inside a route's `beforeLoad`.
 *
 * Throws `redirect({ href, headers: { 'Set-Cookie': ... } })` so the new
 * session cookie is propagated through the SSR page response — the gap
 * `selectTenantServerFn` leaves on initial loads of a foreign-tenant URL.
 * Mirrors the pattern `getUserInfo` already uses for auth redirects.
 *
 * The cookie value is NEVER placed in the response body, so the
 * `httpOnly` protection on the session cookie (containing access /
 * refresh tokens) is preserved end-to-end.
 */
export const switchTenantForRouteServerFn = createServerFn({ method: 'POST' })
  .middleware([sessionMiddleware])
  .inputValidator(z.object({ tenant_id: z.string(), redirect_to: z.string() }))
  .handler(async ({ data: { tenant_id, redirect_to }, context: { session } }) => {
    // Defensive no-op when the session already matches the requested tenant.
    if (session.tenantId === tenant_id) return;

    throw redirect({
      href: redirect_to,
      headers: updateSessionDataHeaders(new Headers(), { ...session, tenantId: tenant_id }),
    });
  });
